import {CBARSurface, CBARSurfaceProperties} from "./CBARSurface";
import {CBAREventType, CBARMouseEvent, CBARSurfaceType, CBARToolMode, getSurfaceType} from "../CBARTypes";
import {CBARObject3DContainer} from "./CBARObject3DContainer";
import {CBARObject3D, CBARObject3DProperties} from "./CBARObject3D";
import * as THREE from "three";
import {PerformanceLogger} from "../internal/GlobalLogger";
import {RenderOptions} from "./CBARScene";
import {CBARStaticSurface} from "./CBARStaticSurface";

export interface CBARSceneGeometryProperties extends CBARObject3DProperties {
    verticalAxis?:"y"|"z"
    surfaces:CBARSurfaceProperties[]
}

export class CBARSceneGeometry extends CBARObject3DContainer<CBARSurface> {

    private _surfaces: { [id: string] : CBARSurface; } = {};

    public get values()  {
        return this._surfaces
    }

    public get surfaces()   {
        return Object.values(this._surfaces)
    }

    public add(surface:CBARSurface) {
        if (surface.id) {
            this._surfaces[surface.id] = surface;
            surface.addToScene()
        }
    }

    public containsKey(key:string) : boolean {
        return this._surfaces.hasOwnProperty(key);
    }

    public remove(surface:CBARSurface) {
        if (!this.containsKey(surface.id)) return;

        surface.removeFromScene();
        delete this._surfaces[surface.id]
    }

    public length(): number {
        return Object.keys(this._surfaces).length
    }

    public first() {
        const length = this.length();
        if (length) {
            return this.all()[0];
        }
    }

    public last() {
        const length = this.length();
        if (length) {
            return this.all()[length-1];
        }
    }

    public all() {
        return Object.values(this._surfaces)
    }


    public clearAll() {
        Object.values(this._surfaces).forEach(surface => {
            surface.removeFromScene();
            surface.unload()
        });
        this._surfaces = {}
    }

    // @ts-ignore
    load(basePath:string|undefined, json:CBARSceneGeometryProperties, groundRotation:number, surfaceTypes?:CBARSurfaceType[], room?:string|null|undefined, subroom?:string|null|undefined) : Promise<CBARSceneGeometry> {

        const promises:Promise<CBARStaticSurface>[] = [];

        if (json.surfaces) {
            for (let i = 0; i < json.surfaces.length; i++) {
                const surfaceJSON = json.surfaces[i] as CBARSurfaceProperties;
                const surfaceType = getSurfaceType(surfaceJSON.type)

                if (surfaceTypes && surfaceTypes.indexOf(surfaceType) < 0) {
                    //console.warn(`Skipping surface ${surfaceJSON.type}`)
                    continue; //skip
                }

                const maskIndex = surfaceJSON.maskIndex === undefined ? i : surfaceJSON.maskIndex;
                const surface = new CBARStaticSurface(this.context, maskIndex);

                promises.push(surface.loadStatic(basePath, surfaceJSON, json.verticalAxis, groundRotation, room, subroom))
            }
        }

        return new Promise<CBARSceneGeometry>((resolve, reject) => {
            Promise.all(promises).then((result)=>{
                result.forEach((surface)=>{
                    this.add(surface as CBARSurface)
                });
                resolve(this)
            }).catch(error=>{
                reject(error)
            })
        })
    }

    public data() : any {

        const surfaceJSON:any[] = [];

        for (const key in this._surfaces) {
            const surface = this._surfaces[key];
            surfaceJSON.push(surface.data())
        }

        return {
            surfaces:surfaceJSON
        }
    }

    public render(options:RenderOptions) {
        Object.values(this._surfaces).forEach(surface => {
            surface.render(options)
        })
    }

    protected getObject3D(event: CBARMouseEvent): CBARObject3D<CBARSurface> | undefined {
        return event.surface as CBARObject3D<CBARSurface> | undefined
    }

    get description() : string {
        return "Scene Geometry"
    }

    sceneLoaded() {
        PerformanceLogger("Scene geometry loading", ()=>{
            Object.values(this.values).forEach(object=>{
                object.sceneLoaded()
            })
        });
    }

    toolModeChanged(mode:CBARToolMode) {
        Object.values(this.values).forEach(object=>{
            object.toolModeChanged(mode)
        })
    }

    getEventObjects(eventType:CBAREventType) : THREE.Object3D[] {
        let objects: THREE.Object3D[] = [];
        Object.values(this._surfaces).forEach(surface => {
            if (surface.receivesEvents) {
                objects.push(surface.renderObject)
            }
        });
        return objects
    }
}
